import React from "react";
import { Box, Card, CardContent, Tooltip, Typography } from "@mui/material";
import { chartAccent } from "../theme";

const visuallyHidden = {
  position: "absolute",
  width: 1,
  height: 1,
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
  whiteSpace: "nowrap",
};

// Smallest 1/2/5 × 10^n that is >= value (and at least 1).
function niceMax(value) {
  if (value <= 1) return 1;
  const magnitude = 10 ** Math.floor(Math.log10(value));
  return [1, 2, 5, 10].map((m) => m * magnitude).find((v) => v >= value);
}

const PLOT_HEIGHT = 150;

// Single-series monthly column chart. data: [{ key, label, longLabel, count }]
export default function MonthlyColumns({ title, subtitle, data, unit }) {
  const top = niceMax(Math.max(0, ...data.map((d) => d.count)));
  const ticks = top % 2 === 0 ? [top, top / 2, 0] : [top, 0];
  const total = data.reduce((sum, d) => sum + d.count, 0);
  const latest = data[data.length - 1];

  return (
    <Card>
      <CardContent>
        <Typography variant="subtitle2">{title}</Typography>
        <Typography variant="caption" color="text.secondary">
          {subtitle || `${total} in the last ${data.length} months`}
        </Typography>

        <Box
          sx={{ position: "relative", height: PLOT_HEIGHT, mt: 3, ml: 4 }}
          aria-hidden
        >
          {ticks.map((t) => (
            <Box
              key={t}
              sx={{
                position: "absolute",
                left: 0,
                right: 0,
                bottom: `${(t / top) * 100}%`,
                borderTop: 1,
                borderColor: "divider",
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  position: "absolute",
                  right: "100%",
                  mr: 1,
                  top: -9,
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {t}
              </Typography>
            </Box>
          ))}
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "flex-end",
              gap: "2px",
            }}
          >
            {data.map((d) => {
              const isLatest = d === latest;
              return (
                <Tooltip
                  key={d.key}
                  title={`${d.longLabel}: ${d.count} ${unit}`}
                  placement="top"
                  arrow
                >
                  <Box
                    sx={{
                      flex: 1,
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "flex-end",
                      borderRadius: 1,
                      "&:hover": { bgcolor: "action.hover" },
                    }}
                  >
                    {isLatest && d.count > 0 && (
                      <Typography
                        variant="caption"
                        sx={{ fontWeight: 600, lineHeight: 1.6 }}
                      >
                        {d.count}
                      </Typography>
                    )}
                    <Box
                      sx={{
                        width: "100%",
                        maxWidth: 24,
                        height: `${(d.count / top) * 100}%`,
                        minHeight: d.count ? 2 : 0,
                        bgcolor: chartAccent,
                        borderRadius: "4px 4px 0 0",
                      }}
                    />
                  </Box>
                </Tooltip>
              );
            })}
          </Box>
        </Box>
        <Box sx={{ display: "flex", gap: "2px", ml: 4, mt: 0.5 }} aria-hidden>
          {data.map((d, i) => (
            <Typography
              key={d.key}
              variant="caption"
              color="text.secondary"
              sx={{
                flex: 1,
                textAlign: "center",
                fontSize: 11,
                // Every other month on phones so labels don't collide.
                visibility: {
                  xs: (data.length - 1 - i) % 2 ? "hidden" : "visible",
                  sm: "visible",
                },
              }}
            >
              {d.label}
            </Typography>
          ))}
        </Box>

        <Box component="table" sx={visuallyHidden}>
          <caption>{title}</caption>
          <tbody>
            {data.map((d) => (
              <tr key={d.key}>
                <th scope="row">{d.longLabel}</th>
                <td>{d.count}</td>
              </tr>
            ))}
          </tbody>
        </Box>
      </CardContent>
    </Card>
  );
}
